import { useState, useEffect } from "react";
import axios from "axios";
import { Activity, Loader2, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { FALLBACK_IMS_RAW } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_TYPES = [
  { value: "", label: "All Sources" },
  { value: "socmint", label: "SOCMINT" },
  { value: "imint", label: "IMINT" },
  { value: "humint", label: "HUMINT" },
  { value: "sigint", label: "SIGINT" },
  { value: "cyber_threat", label: "Cyber Threat" },
  { value: "geospatial", label: "Geospatial" },
];

export default function ImsQuery() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState("");

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (sourceFilter) params.source_type = sourceFilter;

      const res = await axios.get(`${API}/ims/raw`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecords(res.data.length > 0 ? res.data : FALLBACK_IMS_RAW);
    } catch (err) {
      setRecords(FALLBACK_IMS_RAW);
      toast.error("Failed to fetch records. Showing demo data.");
    }
    setLoading(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              RAW DATA
            </h1>
            <p className="text-slate-600">Browse ingested raw intelligence records</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); }}>
          <SelectTrigger className="w-48 rounded-none bg-app border-slate-300">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            {SOURCE_TYPES.map((st) => (
              <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={fetchRecords} size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-500">
          <Activity className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : records.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No raw records found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 p-4 hover:border-emerald-300 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                    {r.source_type}
                  </span>
                  <span className={`text-xs font-medium ${
                    r.status === "normalised" ? "text-emerald-600" : "text-amber-600"
                  }`}>
                    {r.status}
                  </span>
                </div>
                <span className="text-xs text-slate-400">{r.hash?.slice(0, 12)}...</span>
              </div>
              <div className="text-xs text-slate-400 mb-2">
                {r.ingestion_time?.slice(0, 19).replace("T", " ")}
              </div>
              <details>
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                  View payload
                </summary>
                <pre className="mt-2 p-3 bg-app text-xs font-mono whitespace-pre-wrap max-h-48 overflow-auto">
                  {JSON.stringify(r.raw_payload, null, 2)}
                </pre>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
